import { Card } from "primereact/card";
import { useParams } from "react-router-dom";

import CustomeButton from "~/components/Button";
import AuditSidebar from "~/components/audit-sidebar/AuditSidebar";
import API_ROUTES from "~/api-routes";
import PermissionGate from "~/components/PermissionGate";
import FileUploadModal from "~/components/Processes/conversations/FileUploadModal";

import CoreEntityAttachmentTable from "./CoreEntityAttachmentTable";
import useCoreEntityAttachments from "./useCoreEntityAttachments";
import Notification from "../notification/Notification";
import TemporaryNotification from "~/components/toast-notification/AttachmentTemporaryNotification";
import DocumentConfirmationDialog from "~/components/confirmation-dialog/DocumentConfirmationDialog";
import AttachmentSuccess from "~/components/toast-notification/AttachmentSuccess";
import "./core-entity-attachments.scss";
import { mergeCategories } from "~/Pages/Requests/components/new-request-modal/utils";
import { useEffect, useRef, useState } from "react";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";
import BlobFilter from "~/components/BlobFilter";
import FilterChip from "~/components/FilterChip";
import ConfirmationModal from "~/components/modals/confirmation-modal/ConfirmationModal";
import ToastMessage from "~/components/ToastMessage";
import FolderListSection from "~/components/folder-list-section/FolderListSection";
import ProfileAttachmentsBreadcrumb from "~/components/profile-attachments-breadcrumb/ProfileAttachmentsBreadcrumb";

const CoreEntityAttachments = () => {
  const { id } = useParams();

  const {
    attachments,
    loading,
    setLoading,
    page,
    rows,
    totalRecords,
    onPageChange,
    isDataAvailable,
    entitydata,
    showUploadModal,
    fileSupported,
    setShowUploadModal,
    uploadFileHandler,
    requestdata,
    isFileUploaded,
    successMessage,
    setSuccessMessage,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
    first,
    handleDeleteDocument,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    handleRestoreAttachment,
    temporaryMessage,
    isAttachmentLoader,
    setTemporaryMessage,
    restoreMessage,
    setRstoreMessage,
    role,
    selectedItems,
    filterData,
    setFilterData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    filterLoading,
    onSortOrderChange,
    onFilterApply,
    allData,
    filterPageChange,
    currentPage,
    fetchOfficerAttachments,
    unpublishAttachmentsFromEntityInfo,
    categoryFolders,
    selectedCategory,
    setSelectedCategory,
    handleCategoryFolderClick,
    displayAttachments,
  } = useCoreEntityAttachments();
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedFileForPublish, setSelectedFileForPublish] = useState(null);
  const [isUnpublish, setIsUnpublish] = useState(false);
  const toastRef = useRef(null);

  const auditLabels = () => {
    return {
      create: "File Uploaded",
      update: "Address Updated",
      delete: "File(s) Deleted",
    };
  };

  const publishHandler = (showModal: boolean, file) => {
    setSelectedFileForPublish(file);
    setShowPublishModal(showModal);
    setIsUnpublish(false);
  };
  const unpublishHandler = (showModal: boolean, file) => {
    setSelectedFileForPublish(file);
    setShowPublishModal(showModal);
    setIsUnpublish(true);
  };

  const unpublishFileHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        containerName: selectedFileForPublish?.containerName,
        blobName: selectedFileForPublish?.blobName,
      };
      const response = await unpublishAttachmentsFromEntityInfo(payload);
      await fetchOfficerAttachments();
      setSelectedFileForPublish(null);
      setShowPublishModal(false);
      if (response && response.success) {
        // Show toast notification
        toastRef.current?.show({
          severity: "success",
          summary: `"${selectedFileForPublish?.displayName}" was unpublished from Entity Files.`,
          life: 5000,
          icon: "pi pi-check",
        });
      }
    } catch (error) {
      console.error("Error unpublishing file from entity info:", error);
      toastRef.current?.show({
        severity: "error",
        summary: `Something went wrong while unpublishing "${selectedFileForPublish?.displayName}". Please try again.`,
        life: 5000,
        icon: "pi pi-times-circle",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="gen-card h-full">
      <div className="flex justify-content-between align-items-center mb-5">
        <div className="flex-1">
          <ProfileAttachmentsBreadcrumb
            selectedCategory={selectedCategory}
            onBackToCategories={() => setSelectedCategory(null)}
            onRefetch={CoreEntityAttachments}
          />
        </div>
        <div className="flex gap-2">
          <PermissionGate
            resource="audittrail"
            action="view"
            resourceAttr={{ entity_id: id }}
          >
            <CustomeButton
              label="View History"
              icon="pi pi-history"
              iconPos="right"
              onClick={() => setAuditSidebarVisible(true)}
            />
          </PermissionGate>
          <BlobFilter
            onApply={({ value, group }) => {
              setFilterData(value);
            }}
            onRemove={(fn) => (removeFnRef.current = fn)}
            onFilterChange={({ value, grouped }) => {
              handleFilterChange(grouped);
            }}
            showEntity={false}
            showServiceLine={false}
            showYears={false}
            serviceLine={"Entities +"}
            disabled={!isDataAvailable}
          />
          <PermissionGate
            resource="entity"
            action="modify"
            resourceAttr={{ entity_id: id }}
          >
            <CustomeButton
              label="Upload File"
              variant="contained"
              icon="pi pi-upload"
              iconPos="right"
              onClick={() => {
                setShowUploadModal(true);
              }}
            />
          </PermissionGate>
        </div>
      </div>
      <FilterChip
        filterData={filterData}
        removeFilter={(id) => {
          handleRemoveFilter(id);
        }}
      />
      <Notification
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
      />
      <TemporaryNotification
        onUndo={handleRestoreAttachment}
        successMsg={temporaryMessage}
        setMessages={setTemporaryMessage}
      />
      <AttachmentSuccess
        successMsg={restoreMessage}
        setMessages={setRstoreMessage}
      />
      <div className="mt-4">
        {!selectedCategory && !selectedItems.length ? (
          <FolderListSection
            folders={categoryFolders}
            loading={loading}
            onFolderClick={handleCategoryFolderClick}
            showEmptyState={true}
            emptyMessage="No files here yet"
          />
        ) : (
          <>
            <CoreEntityAttachmentTable
              attachments={
                selectedItems.length
                  ? allData
                  : selectedCategory
                    ? displayAttachments
                    : attachments
              }
              loading={selectedItems.length ? filterLoading : loading}
              page={selectedItems.length ? currentPage : page}
              rows={rows}
              first={selectedItems.length ? page - 1 : first}
              totalRecords={
                selectedItems.length
                  ? allData.length
                  : selectedCategory
                    ? displayAttachments.length
                    : attachments?.length
              }
              onPageChange={
                selectedItems.length
                  ? (e) => {
                      filterPageChange(e.page + 1);
                    }
                  : onPageChange
              }
              isDataAvailable={
                selectedItems.length
                  ? allData.length
                  : selectedCategory
                    ? displayAttachments.length
                    : isDataAvailable
              }
              setOpenDialog={setShowUploadModal}
              handleAttachmentDelete={handleDeleteDocument}
              onSortOrderChange={
                selectedItems?.length ? onSortOrderChange : undefined
              }
              isFilterApplied={selectedItems.length}
              onRefetch={
                selectedItems?.length ? onFilterApply : fetchOfficerAttachments
              }
              publishHandler={publishHandler}
              unpublishHandler={unpublishHandler}
              entityId={entitydata?.id}
            />
          </>
        )}
      </div>
      <FileUploadModal
        entitydata={entitydata}
        showModal={showUploadModal}
        fileSupported={fileSupported}
        hideUploadModal={() => setShowUploadModal(false)}
        setUploadedFile={uploadFileHandler}
        getService={() => {
          return { name: "Core Entity Information" };
        }}
        requestdata={requestdata}
        loading={!isFileUploaded}
        close={false}
        relatedType={"ENTITIES_PLUS"}
        categories={mergeCategories(["Entities +"], role)}
      />

      {isAuditSidebarVisible && (
        <AuditSidebar
          heading="Attachments"
          endpoint={API_ROUTES.FETCH_ATTACHMENT_AUDIT}
          id={id}
          objectType="ENTITIES_PLUS"
          setSidebarToggle={setAuditSidebarVisible}
          sidebarToggle={isAuditSidebarVisible}
          labels={auditLabels}
        />
      )}
      {attachmentConfirmModal && (
        <DocumentConfirmationDialog
          visible={attachmentConfirmModal}
          closeModal={() => setAttachmentConfirmModal(false)}
          handleSave={handleConfirmDelete}
          isLoader={isAttachmentLoader}
        />
      )}

      {showPublishModal && (
        <ConfirmationModal
          visible={showPublishModal}
          closeModal={() => setShowPublishModal(false)}
          title={
            isUnpublish
              ? "Undo the publication of this file in the Core Entity Files?"
              : "Publish this file to Core Entity Files?"
          }
          subtitle={
            isUnpublish
              ? "This will remove the reference from Core Entity Files. The document will remain available in the process."
              : "This file will appear in Core Entity Files. If you delete it here, it will also be removed from Core Entity Files, and vice versa."
          }
          confirmHandler={isUnpublish ? unpublishFileHandler : () => {}}
          confirmLabel={"Confirm"}
          isLoading={loading}
          variant={isUnpublish ? "remove" : "contained"}
          isUnpublish={isUnpublish}
        />
      )}
      <ToastMessage ref={toastRef} />
    </Card>
  );
};

export default CoreEntityAttachments;
